import { z } from 'zod';
import {
  ORGANIZATION_FORM_KEYS,
  ORGANIZATION_FORM_VALIDATION,
} from '@/consts/organizationForm';
import { isFileSupportedImage, checkFileSize } from '@/utils/helpers/fileHelpers';

const {
  NAME, COUNTRY, STREET, PROVINCE, POSTAL, OWNER_EMAIL, LOGO,
} = ORGANIZATION_FORM_KEYS;

const {
  NAME_MIN_LENGTH,
  NAME_MAX_LENGTH,
  STREET_MIN_LENGTH,
  PROVINCE_MIN_LENGTH,
  POSTAL_MIN_LENGTH,
  POSTAL_MAX_LENGTH,
  LOGO_MAX_SIZE_MB,
} = ORGANIZATION_FORM_VALIDATION;

export type OrganizationFormVariant = 'create' | 'update';

interface OrganizationValidationMessages {
  NAME_REQUIRED: string;
  NAME_MIN: string;
  NAME_MAX: string;
  STREET_MIN: string;
  PROVINCE_MIN: string;
  POSTAL_MIN: string;
  POSTAL_MAX: string;
  OWNER_EMAIL_REQUIRED: string;
  OWNER_EMAIL_INVALID: string;
  LOGO_INVALID_TYPE: string;
  LOGO_SIZE_EXCEEDED: string;
}

export const buildOrganizationFormSchema = (
  messages: OrganizationValidationMessages,
  options?: {
    ownerEmailRequired?: boolean;
  },
) => {
  const ownerEmailRequired = options?.ownerEmailRequired ?? true;

  return z.object({
    [NAME]: z.string()
      .nonempty(messages.NAME_REQUIRED)
      .min(NAME_MIN_LENGTH, messages.NAME_MIN)
      .max(NAME_MAX_LENGTH, messages.NAME_MAX),
    [COUNTRY]: z.string().optional(),
    [STREET]: z.string().optional().refine(
      (val) => !val || val.length >= STREET_MIN_LENGTH,
      { message: messages.STREET_MIN },
    ),
    [PROVINCE]: z.string().optional().refine(
      (val) => !val || val.length >= PROVINCE_MIN_LENGTH,
      { message: messages.PROVINCE_MIN },
    ),
    [POSTAL]: z.string().optional()
      .refine(
        (val) => !val || val.length >= POSTAL_MIN_LENGTH,
        { message: messages.POSTAL_MIN },
      )
      .refine(
        (val) => !val || val.length <= POSTAL_MAX_LENGTH,
        { message: messages.POSTAL_MAX },
      ),
    [OWNER_EMAIL]: ownerEmailRequired
      ? z.string()
        .nonempty(messages.OWNER_EMAIL_REQUIRED)
        .email(messages.OWNER_EMAIL_INVALID)
      : z.union([
        // For PATCH/update, the field is empty.
        z.string().email(messages.OWNER_EMAIL_INVALID),
        z.literal(''),
      ]),
    [LOGO]: z.union([z.instanceof(File), z.null()]).optional()
      .refine(
        (file) => !file || isFileSupportedImage(file),
        { message: messages.LOGO_INVALID_TYPE },
      )
      .refine(
        (file) => !file || checkFileSize(file, LOGO_MAX_SIZE_MB),
        { message: messages.LOGO_SIZE_EXCEEDED },
      ),
  });
};

type IOrganizationFormSchema = ReturnType<typeof buildOrganizationFormSchema>;
export type IOrganizationFormData = z.infer<IOrganizationFormSchema>;
